import { ClassValue, clsx } from 'clsx'
import { twMerge } from 'tailwind-merge'

/**
 * Combina y optimiza nombres de clases CSS, especialmente útil para Tailwind CSS.
 *
 * @description
 * Esta función utiliza dos herramientas principales:
 * - `clsx`: Para combinar condicionalmente nombres de clase
 * - `twMerge`: Para resolver conflictos entre clases de Tailwind
 *
 * @param {...ClassValue} inputs - Lista de clases CSS que pueden ser:
 *   - Strings: 'btn-primary'
 *   - Objetos: { 'btn-active': isActive }
 *   - Arrays: ['btn', isLarge && 'btn-lg']
 *   - Anidados: ['btn', ['primary', ['large']]]
 *
 * @returns {string} Cadena optimizada de nombres de clase, con:
 *   - Duplicados eliminados
 *   - Conflictos resueltos (el último valor prevalece)
 *   - Clases Tailwind fusionadas correctamente
 *
 * @example
 * // Uso básico
 * cn('p-4', 'bg-blue-500')
 * // => 'p-4 bg-blue-500'
 *
 * @example
 * // Con condiciones
 * cn('p-4', isActive && 'bg-blue-500')
 * // => 'p-4 bg-blue-500' (si isActive es true)
 * // => 'p-4' (si isActive es false)
 *
 * @example
 * // Resolviendo conflictos
 * cn('p-2 p-4', 'mt-2')
 * // => 'p-4 mt-2'
 *
 * @see {@link https://github.com/lukeed/clsx clsx} - Para más detalles sobre combinación de clases
 * @see {@link https://github.com/dcastil/tailwind-merge tailwind-merge} - Para fusión de clases Tailwind
 */
export function cn(...inputs) {
  return twMerge(clsx(inputs))
}

/**
 * @function safeStringify
 * @description
 * Formatea un título de página eliminando caracteres especiales y capitalizando
 * el primer carácter de cada palabra.
 * @param {Object} obj - Objeto a formatear
 * @returns {string} - Cadena JSON formateada
 * @example
 *
 * safeStringify({ key: "value" }) // => '{"key":"value"}'
 */
export const safeStringify = obj => {
  return JSON.stringify(obj).replace(/"/g, '&quot;')
}
